<template>
  <div class="page-layout">
    <div class="page-content">
      <AppBreadcrumbs :items="breadcrumbItems" />
      <div class="page-header-title">
        <UserAvatar v-if="author" :user="author" size="md" />
        <h1>{{ pageTitle }}</h1>
      </div>

      <div v-if="loading" class="panel">
        <p class="empty-state">Загрузка...</p>
      </div>

      <div v-else-if="error" class="panel">
        <p class="error-message">{{ error }}</p>
      </div>

      <div v-else class="panel">
        <TripList
          :trips="trips"
          empty-text="У автора пока нет публичных поездок."
          @select="openTrip"
        />
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, onMounted, ref, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import TripList from '../components/TripList.vue'
import UserAvatar from '../components/UserAvatar.vue'
import AppBreadcrumbs from '../components/AppBreadcrumbs.vue'
import { fetchUserPublicTrips, fetchUserPublicTripsByUsername } from '../api/trips'
import { userDisplayName } from '../utils/userDisplay'
import { redirectToLoginIfUnauthorized } from '../utils/profileAccess'

const route = useRoute()
const router = useRouter()

const author = ref(null)
const trips = ref([])
const loading = ref(true)
const error = ref('')

const pageTitle = computed(() => {
  if (author.value) {
    return `Публичные поездки — ${userDisplayName(author.value)}`
  }
  return 'Публичные поездки'
})

const breadcrumbItems = computed(() => {
  const items = [{ label: 'Поездки', to: { name: 'trips-home' } }]

  if (author.value) {
    if (author.value.username) {
      items.push({
        label: userDisplayName(author.value),
        to: { name: 'public-profile', params: { username: author.value.username } },
      })
    } else {
      items.push({ label: userDisplayName(author.value) })
    }
  }

  items.push({ label: 'Публичные поездки' })
  return items
})

async function loadAuthorTrips() {
  loading.value = true
  error.value = ''
  author.value = null
  trips.value = []
  let redirecting = false

  try {
    const response = route.params.username
      ? await fetchUserPublicTripsByUsername(route.params.username)
      : await fetchUserPublicTrips(route.params.id)
    author.value = response.data.author
    trips.value = response.data.trips
  } catch (err) {
    if (redirectToLoginIfUnauthorized(err, router, route)) {
      redirecting = true
      return
    }

    if (err.response?.status === 404) {
      error.value = route.params.username ? 'Профиль не найден или скрыт' : 'Автор не найден'
    } else {
      error.value = 'Не удалось загрузить поездки автора'
    }
  } finally {
    if (!redirecting) {
      loading.value = false
    }
  }
}

function openTrip(id) {
  router.push({ name: 'trip', params: { id } })
}

onMounted(loadAuthorTrips)
watch(() => [route.params.id, route.params.username], loadAuthorTrips)
</script>
